<template>
  <div>
    <!-- Mobile: Category Links -->
    <div class="sm:hidden">
      <MagazineMobileCategoryLinks :categories="magazineCategories" />
    </div>

    <!-- Desktop: Category Link Chips -->
    <div class="hidden sm:block">
      <Swiper
        ref="categorySlider"
        v-bind="swiperOptions"
        class="category-swiper"
      >
        <SwiperSlide
          v-for="category in magazineCategories"
          :key="category.slug"
          class="!w-auto"
        >
          <a
            :href="fullLocalizedUrl(`/magazine/${category.slug}`)"
            :data-test-id="`category-link-${category.slug}`"
            class="flex-shrink-0"
          >
            <Chip
              :selected="isCurrentCategory(category.slug)"
              :classes="'!m-0'"
            >
              <template #prefix>
                <img
                  v-if="category.icon"
                  :src="category.icon"
                  :alt="category.name"
                  class="w-4 h-4"
                />
              </template>
              <span class="text-xs font-medium whitespace-nowrap flex-shrink-0">
                {{ category.name }}
              </span>
            </Chip>
          </a>
        </SwiperSlide>
      </Swiper>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Mousewheel } from 'swiper/modules'
import 'swiper/css'
// Import necessary composables and getters
import { useStrapiArticleCategories } from '~/composables/useStrapiArticleCategories'
import { articleCategoryGetters } from '~/utils/articleCategoryGetters'
import MagazineMobileCategoryLinks from '~/components/magazine/MagazineMobileCategoryLinks.vue'
import Chip from '~/components/common/Chip.vue'

const route = useRoute()
const fullLocalizedUrl = useFullLocalizedUrl()
const categorySlider = ref<any>(null)

// Swiper options for full-width slider
const swiperOptions = computed(() => {
  return {
    modules: [Mousewheel],
    slidesPerView: 'auto' as const,
    spaceBetween: 8,
    freeMode: true,
    mousewheel: {
      sensitivity: 1,
      releaseOnEdges: false,
      eventsTarget: 'container',
    },
  }
})

// Fetch categories from Strapi
const { data: categoriesData } = await useStrapiArticleCategories()

// Dynamic magazine categories from Strapi
const magazineCategories = computed(() => {
  const categories = categoriesData.value
    ? categoriesData.value
        .map((category: any) => ({
          slug: articleCategoryGetters.getSlug(category) || '',
          name: articleCategoryGetters.getTitle(category) || '',
          icon: articleCategoryGetters.getIcon(category) || null,
        }))
        .filter((category: any) => category.slug && category.name)
    : []

  return categories
})

// Check if a category is currently active
const isCurrentCategory = (categorySlug: string) => {
  return route.params.categorySlug === categorySlug
}
</script>

<style lang="postcss" scoped>
.scrollbar-hide {
  -ms-overflow-style: none; /* Internet Explorer 10+ */
  scrollbar-width: none; /* Firefox */
}
.scrollbar-hide::-webkit-scrollbar {
  display: none; /* Safari and Chrome */
}

.category-swiper {
  overflow: visible !important;

  :deep(.swiper-wrapper) {
    height: auto !important;
  }
}
</style>
